import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Calendar, CalendarRange, TrendingUp, Sparkles, Loader2 } from 'lucide-react';
import { DailyCheckInWidget, WellnessJournalWidget } from './EngagementWidgets';
import WeekBackfillStrip from './WeekBackfillStrip';
import SlotCompletionSparkline from './SlotCompletionSparkline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'today', label: 'Today', icon: Calendar },
  { id: 'week', label: 'This Week', icon: CalendarRange },
  { id: 'trends', label: 'Trends', icon: TrendingUp },
];

const METRIC_LABELS = {
  energy: { label: 'Energy', emoji: '⚡' },
  sleep_quality: { label: 'Sleep', emoji: '🌙' },
  mood: { label: 'Mood', emoji: '😊' },
  digestion: { label: 'Digestion', emoji: '🍃' },
  focus: { label: 'Focus', emoji: '🎯' },
};

// ── Trends sub-component: averages + 30-day bars + correlation insights ──
function TrendsPanel({ getAuthHeader }) {
  const [trends, setTrends] = useState(null);
  const [correlation, setCorrelation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [tRes, cRes] = await Promise.all([
          axios.get(`${API}/wellness/trends?days=${period}`, { headers: getAuthHeader() }),
          axios.get(`${API}/wellness/correlation`, { headers: getAuthHeader() }).catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setTrends(tRes.data);
          setCorrelation(cRes.data);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [period, getAuthHeader]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-border p-10 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  const hasData = trends && trends.total_logged_days > 0;

  return (
    <div className="space-y-4" data-testid="trends-panel">
      {/* Period switcher */}
      <div className="bg-white rounded-2xl border border-border p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="font-semibold text-sm text-[#1A1A1A] flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-indigo-600" />
              Wellness Trends
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {hasData ? `${trends.total_logged_days} days logged in the last ${trends.period_days}` : 'Start logging to see trends'}
            </p>
          </div>
          <div className="flex gap-1 bg-muted rounded-lg p-1">
            {[7, 30, 90].map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  period === p ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-muted-foreground'
                }`}
                data-testid={`trends-period-${p}`}
              >
                {p}d
              </button>
            ))}
          </div>
        </div>

        {hasData ? (
          <div className="space-y-3">
            {Object.entries(METRIC_LABELS).map(([key, meta]) => {
              const avg = trends.averages[key] || 0;
              const pct = Math.round((avg / 5) * 100);
              const color = avg >= 3.5 ? 'bg-emerald-500' : avg >= 2.5 ? 'bg-amber-400' : 'bg-orange-400';
              return (
                <div key={key}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-[#1A1A1A]">{meta.emoji} {meta.label}</span>
                    <span className="text-xs font-bold text-[#1A1A1A]">{avg}/5</span>
                  </div>
                  <div className="bg-muted rounded-full h-2 overflow-hidden">
                    <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Log at least a few days to start seeing trends here.
          </div>
        )}
      </div>

      {/* Per-supplement correlation cards */}
      {correlation && correlation.per_supplement && correlation.per_supplement.length > 0 && (
        <div className="bg-white rounded-2xl border border-border p-5" data-testid="per-supplement-insights">
          <h3 className="font-semibold text-sm text-[#1A1A1A] flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-violet-600" />
            What&apos;s working for you
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            Per-supplement impact on your wellness over the last 90 days.
          </p>
          <div className="space-y-2.5">
            {correlation.per_supplement.map((s, i) => {
              const positive = s.diff_percent > 0;
              const pillColor = positive
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-orange-100 text-orange-700';
              const barColor = positive ? 'bg-emerald-500' : 'bg-orange-400';
              const onPct = Math.round((s.on_avg / 5) * 100);
              const offPct = Math.round((s.off_avg / 5) * 100);
              return (
                <div
                  key={i}
                  className="rounded-xl border border-border p-3 hover:border-violet-200 transition-colors"
                  data-testid={`supplement-insight-${i}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm text-[#1A1A1A] truncate">
                        {s.supplement}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        on {s.metric}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${pillColor}`}>
                      {positive ? '+' : ''}{s.diff_percent}%
                    </span>
                  </div>
                  {/* Mini comparison bars */}
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] w-12 text-muted-foreground">With</span>
                      <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                        <div className={`h-full ${barColor} rounded-full`} style={{ width: `${onPct}%` }} />
                      </div>
                      <span className="text-[11px] font-semibold text-[#1A1A1A] w-10 text-right">{s.on_avg}/5</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] w-12 text-muted-foreground">Without</span>
                      <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                        <div className="h-full bg-slate-300 rounded-full" style={{ width: `${offPct}%` }} />
                      </div>
                      <span className="text-[11px] font-semibold text-muted-foreground w-10 text-right">{s.off_avg}/5</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2 italic">
                    Based on {s.on_days} days with · {s.off_days} days without
                  </p>
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-muted-foreground/80 mt-3 italic leading-relaxed">
            Note: Correlation isn&apos;t causation. Many factors affect wellness — use this as a starting point for conversation with your provider.
          </p>
        </div>
      )}

      {/* Correlation insights (overall — "any supplement day") */}
      {correlation && correlation.has_insights && correlation.insights && correlation.insights.length > 0 && (
        <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-2xl border border-violet-200/70 p-5" data-testid="correlation-insights">
          <h3 className="font-semibold text-sm text-violet-900 flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4" />
            Correlation Insights
          </h3>
          <div className="space-y-2">
            {correlation.insights.slice(0, 4).map((insight, i) => (
              <div key={i} className="text-xs text-violet-800 bg-white/60 rounded-lg p-2.5 leading-relaxed">
                {insight.message || insight}
              </div>
            ))}
          </div>
        </div>
      )}

      {correlation && !correlation.has_insights && correlation.message && (
        <div className="bg-muted/40 border border-dashed border-border rounded-2xl p-5 text-center text-xs text-muted-foreground" data-testid="correlation-placeholder">
          {correlation.message}
        </div>
      )}
    </div>
  );
}

/**
 * Unified tracking hub that groups Daily Check-In + Wellness + Week Backfill +
 * Slot Completion + Trends behind a single tabbed interface. Reduces dashboard
 * clutter on mobile while keeping every flow one tap away.
 */
export default function TrackingHub({ getAuthHeader, supplements }) {
  const [active, setActive] = useState(() => localStorage.getItem('tracking-hub-tab') || 'today');

  const setTab = (id) => {
    setActive(id);
    localStorage.setItem('tracking-hub-tab', id);
  };

  return (
    <div className="space-y-4" data-testid="tracking-hub">
      {/* Tab strip */}
      <div className="bg-white rounded-2xl border border-border p-1.5 flex gap-1" role="tablist">
        {TABS.map(t => {
          const Icon = t.icon;
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-xl text-xs sm:text-sm font-medium transition-all ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              data-testid={`tracking-tab-${t.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18 }}
          className="space-y-4"
        >
          {active === 'today' && (
            <>
              <DailyCheckInWidget getAuthHeader={getAuthHeader} supplements={supplements} />
              <WellnessJournalWidget getAuthHeader={getAuthHeader} />
            </>
          )}
          {active === 'week' && (
            <>
              <WeekBackfillStrip getAuthHeader={getAuthHeader} />
              <SlotCompletionSparkline getAuthHeader={getAuthHeader} />
            </>
          )}
          {active === 'trends' && (
            <TrendsPanel getAuthHeader={getAuthHeader} />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
